import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getCurrentLocation, setLocationPermission } from '../../services/location';
import { logEvent } from '../../services/analytics/eventLogger';
import { ButtonRounded } from '../UISystem/Buttons';
import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';
import { SHADOWS } from '../../styles/UISystem';

// Context-driven copy for the location pre-prompt. Leads with the benefit (no
// generic "Access location" title) and tunes the CTA and dismiss per context.
const COPY = {
  sharing: {
    headline: 'Allow neighborhood sharing',
    note: 'Turn it on or off anytime',
    cta: 'Ok',
    dismiss: 'Maybe later',
  },
  connecting: {
    headline: 'Remember where you meet people',
    note: null,
    cta: 'Ok',
    dismiss: 'Continue without location',
  },
  // The QR screen's ask (user design 2026-08-16) — states the moment it
  // applies to rather than the benefit, because the QR is right there behind
  // it. Rides the bare presentation below.
  qr: {
    headline: 'When connecting with your QR',
    note: null,
    cta: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
        <MapPin size={20} weight="fill" />
        Get location
      </span>
    ),
    dismiss: 'Later',
  },
};
// The new code page's ask (signup E3a; FINALS 5A.8, SFLocationSheet "reused
// whole"): the same bare sheet, titled `When connecting`, its CTA full width
// (every sheet's buttons run edge to edge — share-sheet rules N13).
COPY.code = { ...COPY.qr, headline: 'When connecting' };

/**
 * Location permission pre-prompt ("Access location"). Shown before the browser's
 * native prompt; the copy adapts to where it's invoked from.
 *
 * The permission handling is the point of this component being ONE home: the
 * GPS-fix-before-we-record-the-grant order below is what keeps a dismissed iOS
 * prompt from leaving a stale 'granted' flag (see location-share-silent-failure).
 * Presentation varies by context, that logic never does.
 *
 * Two presentations:
 *   default — dark backdrop, full-width buttons
 *   bare ('qr') — no scrim, content-width pill CTA. Matches the QR photo sheet
 *     it queues behind, and a backdrop over the QR screen tints Safari's
 *     toolbars (ui-design-system §4.8).
 *
 * @param {function} onComplete - (allowed, error?, coords?)
 * @param {'sharing'|'connecting'|'qr'|'code'} [context] - drives copy + presentation
 */
const LocationPrompt = ({ onComplete, context = 'connecting' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const copy = COPY[context] || COPY.connecting;
  const bare = context === 'qr' || context === 'code';
  // The old QR's centred CTA; the new code page's runs edge to edge (N13)
  const narrowCta = context === 'qr';

  // Body scroll lock while the sheet is open (ui-design-system.md §4.7)
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Every show is queryable (device pass 2, 2026-08-08: a re-firing primer
  // could not be traced to its trigger from code alone — this stamps which
  // instance fired, from which screen)
  useEffect(() => {
    logEvent('location_prompt_shown', {
      context,
      screen: sessionStorage.getItem('nav_current_screen') || null,
    });
  }, [context]);

  // Handle user allowing location
  const handleAllowLocation = async () => {
    try {
      console.log('LocationPrompt: User allowed location');
      setIsLoading(true);
      setError(null);

      // Verify the browser actually grants permission by fetching GPS coords
      // BEFORE recording our flag. We only need coords to confirm permission —
      // no Radar API call needed. Coords are passed to onComplete so the caller
      // can reuse them (no second GPS call).
      const coords = await getCurrentLocation();

      // Record permission only now that a real GPS fix confirmed it. Writing this
      // optimistically (before the fix) made a failed first attempt skip the primer
      // on every retry — a silent dead end. See location-share-silent-failure.md.
      setLocationPermission(true);

      // Pass coords so the caller skips its own GPS call
      onComplete(true, null, coords);
    } catch (error) {
      console.error('Error getting location:', error);
      setError('Could not access your location. Please check your device settings.');

      if (error.code === 1) {
        // PERMISSION_DENIED — record the denial (never leave a stale 'granted'),
        // so the primer keeps re-asking instead of landing on a dead button.
        console.log('LocationPrompt: Browser denied permission');
        setLocationPermission(false);
        onComplete(false, error);
      } else {
        // GPS timeout / unavailable — we never wrote 'granted', so the primer
        // re-asks. Pass the error up so the caller can log the real reason.
        onComplete(false, error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user declining location
  const handleDeclineLocation = () => {
    // Don't save denial to localStorage
    console.log('User declined location in our prompt');

    // Call the completion handler with null location
    onComplete(null);
  };

  return (
    <motion.div
      className="location-prompt-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={() => {
        // Backdrop tap = soft dismiss (decline). Ignore while a GPS check is in flight.
        if (!isLoading) handleDeclineLocation();
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        backgroundColor: bare ? 'transparent' : 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <motion.div
        className="location-prompt-sheet"
        initial={{ y: 500 }}
        animate={{ y: 0 }}
        exit={{ y: 500 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          backgroundColor: 'white',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: SHADOWS.TIER3,
          padding: bare
            ? '28px 24px calc(24px + env(safe-area-inset-bottom))'
            : '32px 20px calc(40px + env(safe-area-inset-bottom))',
          textAlign: 'center',
        }}
      >
        <h3
          style={{
            margin: copy.note ? '0 0 8px' : bare ? '0 0 22px' : '0 0 24px',
            color: '#000',
            fontWeight: 700,
            fontSize: bare ? '20px' : '18px',
          }}
        >
          {copy.headline}
        </h3>

        {copy.note && (
          <p style={{ margin: '0 0 24px', color: '#666', fontSize: '15px', lineHeight: '1.4' }}>
            {copy.note}
          </p>
        )}

        {error && <p style={{ color: 'red', margin: '10px 0' }}>{error}</p>}

        <ButtonRounded
          text={copy.cta}
          size={narrowCta ? 'auto' : 'full'}
          onClick={handleAllowLocation}
          disabled={isLoading}
          style={narrowCta ? { minWidth: '210px', margin: '0 auto' } : undefined}
        />
        {/* Wrapped when bare: size="inline" renders an inline <button>, and
            margin-top does not apply to those — the wrapper carries the gap. */}
        {bare ? (
          <div style={{ marginTop: '20px' }}>
            <ButtonRounded
              text={copy.dismiss}
              variant="blank"
              size="inline"
              onClick={handleDeclineLocation}
              disabled={isLoading}
            />
          </div>
        ) : (
          <ButtonRounded
            text={copy.dismiss}
            variant="blank"
            size="full"
            onClick={handleDeclineLocation}
            disabled={isLoading}
            style={{ marginTop: '4px' }}
          />
        )}
      </motion.div>
    </motion.div>
  );
};

export default LocationPrompt;
